/**
 * Página de Regras de Roteamento
 * 
 * Permite criar e gerenciar regras de roteamento automático
 * para distribuição de conversas entre departamentos e agentes.
 * Suporta seleção de inboxes específicas por regra.
 * 
 * v2.5.0 - Sistema de abas Geral/Webhook
 * 
 * @version 2.5.0
 * @date 2026-01-30
 */

import { useState, useEffect, useMemo } from 'react';
import { Plus, Route, Pencil, Trash2, ArrowUp, ArrowDown, ListChecks, Globe, Inbox, Webhook, ToggleLeft, ToggleRight, FolderTree, ChevronDown, Layers } from 'lucide-react';
import { RoutingCategoriesTab } from '@/components/tenant/routing/categories/RoutingCategoriesTab';
import { RoutingCategoryIcon } from '@/components/tenant/routing/categories/RoutingCategoryIcon';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { supabase } from '@/integrations/supabase/client';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CreateRuleDialog } from '@/components/tenant/routing/CreateRuleDialog';
import { EditRuleDialog } from '@/components/tenant/routing/EditRuleDialog';
import { WebhookRulesList } from '@/components/tenant/routing/WebhookRulesList';
import { WebhookRuleDetailDialog } from '@/components/tenant/routing/WebhookRuleDetailDialog';
import { WebhookRuleTestDialog } from '@/components/tenant/routing/WebhookRuleTestDialog';
import { createLogger } from '@/lib/logger';
import type { RoutingRuleWithWebhook } from '@/components/tenant/routing/types';

// Logger para o módulo de Roteamento
const routingLogger = createLogger('RoutingRules');

// Tipos de condição e ação
// v2.3: Adicionado 'webhook' para receber gatilhos de sistemas externos
export type RoutingConditionType = 'keyword' | 'regex' | 'first_message' | 'contact_tag' | 'time_range' | 'manual_execution' | 'webhook';

// Router 2.2: Inclui ações de dados, envio e distribuição
export type RoutingActionType = 
  // Ações de dados (modificam conversa/contato)
  | 'assign_department' 
  | 'assign_agent' 
  | 'add_tag' 
  | 'remove_tag'  // v2.1: Remover tag de contato
  | 'set_priority'
  | 'assign_phase'
  | 'assign_global_queue'  // v2.2: Distribuir via fila global
  // Ações de envio (enviam mensagens via multi-provider)
  | 'send_text'
  | 'send_image'
  | 'send_video'
  | 'send_audio'
  | 'send_document'
  | 'send_link'
  | 'send_button_link'  // v2.3: Botão com link clicável (Z-API send-button-actions)
  | 'send_template';    // v2.4: Envio de template WhatsApp (WABA)

// Interface para regra de roteamento
export interface RoutingRule {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  priority: number;
  condition_type: RoutingConditionType;
  condition_value: Record<string, unknown>;
  action_type: RoutingActionType;
  action_value: Record<string, unknown>;
  inbox_ids: string[] | null;
  stop_on_match: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // v1.1.0: metadados de categoria vindos da RPC get_tenant_routing_rules_full
  category_id?: string | null;
  category_name?: string | null;
  category_color?: string | null;
  category_icon?: string | null;
  category_sort_order?: number | null;
}

// Labels para tipos de condição
export const conditionTypeLabels: Record<RoutingConditionType, string> = {
  keyword: 'Palavra-chave',
  regex: 'Expressão Regular',
  first_message: 'Primeira Mensagem',
  contact_tag: 'Tag do Contato',
  time_range: 'Horário',
  manual_execution: 'Execução Manual',
  webhook: 'Webhook Externo',
};

// Labels para tipos de ação (Router 2.2)
export const actionTypeLabels: Record<RoutingActionType, string> = {
  assign_department: 'Atribuir Departamento',
  assign_agent: 'Atribuir Agente',
  add_tag: 'Adicionar Tag',
  remove_tag: 'Remover Tag',
  set_priority: 'Definir Prioridade',
  assign_phase: 'Atribuir Fase',
  assign_global_queue: 'Fila Global',
  send_text: 'Enviar Texto',
  send_image: 'Enviar Imagem',
  send_video: 'Enviar Vídeo',
  send_audio: 'Enviar Áudio',
  send_document: 'Enviar Documento',
  send_link: 'Enviar Link',
  send_button_link: 'Botão com Link',  // v2.3: Botão CTA clicável
  send_template: 'Template WhatsApp',  // v2.4: Envio de template WABA
};

// Cores para badges de condição
const conditionColors: Record<RoutingConditionType, string> = {
  keyword: 'bg-primary/10 text-primary border-primary/20',
  regex: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  first_message: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  contact_tag: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  time_range: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
  manual_execution: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  webhook: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
};

const RoutingRules = () => {
  const { currentTenant } = useAuth();
  const { isAdmin, canCreate, canEdit, canDelete } = usePermissions();
  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Abre modal via ?new=1 (atalho "Nova ação rápida" do Quick Create do header).
  useAutoOpenFromQuery('1', () => setCreateDialogOpen(true));
  const [editingRule, setEditingRule] = useState<RoutingRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<RoutingRule | null>(null);
  
  // Estado da aba ativa
  const [activeTab, setActiveTab] = useState('general');
  
  // Estados para modal de webhook detalhado
  const [webhookDetailRule, setWebhookDetailRule] = useState<RoutingRuleWithWebhook | null>(null);
  const [webhookTestRule, setWebhookTestRule] = useState<RoutingRuleWithWebhook | null>(null);

  // v1.1.0: Agrupar macros por categoria na aba Geral (persistido em localStorage)
  const [groupByCategory, setGroupByCategory] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem('routing-rules-grouped');
    return stored === null ? true : stored === '1';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('routing-rules-grouped', groupByCategory ? '1' : '0');
    }
  }, [groupByCategory]);

  // Estado aberto/fechado por grupo, persistido por categoria
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = window.localStorage.getItem('routing-rules-category-open-map');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const toggleGroupOpen = (key: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !(prev[key] ?? true) };
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('routing-rules-category-open-map', JSON.stringify(next));
      }
      return next;
    });
  };
  const isGroupOpen = (key: string) => openGroups[key] ?? true;

  // Carrega regras do tenant
  useEffect(() => {
    if (currentTenant?.id) {
      fetchRules();
    }
  }, [currentTenant?.id]);

  // Buscar inboxes para exibir nomes (via RPC server-side segura)
  const { data: inboxesMap } = useQuery({
    queryKey: ['inboxes-map', currentTenant?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_tenant_inboxes_safe', {
        p_tenant_hint: currentTenant!.id,
      });

      if (error) throw error;

      const map: Record<string, string> = {};
      (data || []).forEach((inbox: { id: string; name: string }) => {
        map[inbox.id] = inbox.name;
      });
      return map;
    },
    enabled: !!currentTenant?.id,
  });

  // Carrega regras via RPC server-side (inclui actions[] e allowed_user_ids[])
  const fetchRules = async () => {
    if (!currentTenant?.id) return;

    try {
      const { data, error } = await supabase.rpc('get_tenant_routing_rules_full', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;

      const list = Array.isArray(data) ? data : [];
      setRules(list.map((rule: any) => ({
        ...rule,
        condition_type: rule.condition_type as RoutingConditionType,
        action_type: rule.action_type as RoutingActionType,
        condition_value: rule.condition_value as Record<string, unknown>,
        action_value: rule.action_value as Record<string, unknown>,
      })));
    } catch (error) {
      routingLogger.error('Erro ao carregar regras', error);
      toast.error('Erro ao carregar regras de roteamento');
    } finally {
      setLoading(false);
    }
  };

  // Separar regras por tipo
  const { generalRules, webhookRules } = useMemo(() => {
    const general: RoutingRule[] = [];
    const webhook: RoutingRuleWithWebhook[] = [];
    
    rules.forEach(rule => {
      if (rule.condition_type === 'webhook') {
        webhook.push(rule as unknown as RoutingRuleWithWebhook);
      } else {
        general.push(rule);
      }
    });
    
    return { generalRules: general, webhookRules: webhook };
  }, [rules]);

  // v1.1.0: Agrupa as macros gerais por categoria (preservando ordem de prioridade)
  // Cada grupo expõe { key, name, color, icon, sortOrder, rules }
  // - Regras sem categoria caem em um grupo virtual "__uncategorized__" exibido por último
  // - Ordem dos grupos: sort_order asc, depois nome; "Sem categoria" sempre por último
  const generalRulesGrouped = useMemo(() => {
    type Group = {
      key: string;
      name: string;
      color: string | null;
      icon: string | null;
      sortOrder: number;
      isUncategorized: boolean;
      rules: RoutingRule[];
    };
    const map = new Map<string, Group>();
    generalRules.forEach((rule) => {
      const key = rule.category_id || '__uncategorized__';
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: rule.category_name || 'Sem categoria',
          color: rule.category_color ?? null,
          icon: rule.category_icon ?? null,
          sortOrder: rule.category_sort_order ?? 9999,
          isUncategorized: !rule.category_id,
          rules: [],
        });
      }
      map.get(key)!.rules.push(rule);
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.isUncategorized !== b.isUncategorized) return a.isUncategorized ? 1 : -1;
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name);
    });
  }, [generalRules]);

  // Toggle ativo/inativo via RPC (já invalida trigger_cache server-side)
  const toggleRuleActive = async (rule: RoutingRule) => {
    try {
      const { error } = await supabase.rpc('toggle_routing_rule_active', {
        p_rule_id: rule.id,
        p_tenant_hint: currentTenant?.id,
      });

      if (error) throw error;

      setRules(prev =>
        prev.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r)
      );
      toast.success(`Regra ${!rule.is_active ? 'ativada' : 'desativada'}`);
    } catch (error) {
      routingLogger.error('Erro ao atualizar regra', error);
      toast.error('Erro ao atualizar regra');
    }
  };

  // Trocar prioridade entre duas regras (atômico via RPC)
  const movePriority = async (rule: RoutingRule, direction: 'up' | 'down') => {
    const currentIndex = generalRules.findIndex(r => r.id === rule.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= generalRules.length) return;

    const targetRule = generalRules[targetIndex];

    try {
      const { error } = await supabase.rpc('swap_routing_rule_priority', {
        p_rule_id_a: rule.id,
        p_rule_id_b: targetRule.id,
        p_tenant_hint: currentTenant?.id,
      });

      if (error) throw error;

      await fetchRules();
      toast.success('Prioridade atualizada');
    } catch (error) {
      routingLogger.error('Erro ao mover prioridade', error);
      toast.error('Erro ao atualizar prioridade');
    }
  };

  // Confirmar exclusão via RPC (já invalida trigger_cache server-side)
  const confirmDelete = async () => {
    if (!ruleToDelete) return;

    try {
      const { error } = await supabase.rpc('delete_routing_rule', {
        p_rule_id: ruleToDelete.id,
        p_tenant_hint: currentTenant?.id,
      });

      if (error) throw error;

      setRules(prev => prev.filter(r => r.id !== ruleToDelete.id));
      toast.success('Regra excluída');
    } catch (error) {
      routingLogger.error('Erro ao excluir regra', error);
      toast.error('Erro ao excluir regra');
    } finally {
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  };

  // Descrição da condição
  const getConditionDescription = (rule: RoutingRule): string => {
    const value = rule.condition_value;
    
    if ('logic_operator' in value && 'conditions' in value && Array.isArray(value.conditions)) {
      const config = value as { logic_operator: 'and' | 'or'; conditions: Array<{ type: string; value: Record<string, unknown> }> };
      const operator = config.logic_operator === 'and' ? ' E ' : ' OU ';
      
      const descriptions = config.conditions.map(cond => {
        const typeLabel = conditionTypeLabels[cond.type as RoutingConditionType] || cond.type;
        let valueDesc = '';
        
        switch (cond.type) {
          case 'keyword':
            const kws = (cond.value.keywords as string[]) || [];
            valueDesc = kws.length > 0 ? kws.slice(0, 2).join(', ') : '';
            if (kws.length > 2) valueDesc += ` (+${kws.length - 2})`;
            break;
          case 'time_range':
            valueDesc = `${cond.value.start || '??'}–${cond.value.end || '??'}`;
            break;
          case 'first_message':
            valueDesc = 'Nova';
            break;
          case 'webhook':
            valueDesc = 'Externo';
            break;
          default:
            break;
        }
        
        return valueDesc ? `${typeLabel}: ${valueDesc}` : typeLabel;
      });
      
      return descriptions.join(operator);
    }
    
    switch (rule.condition_type) {
      case 'keyword':
        const keywords = (value.keywords as string[]) || [];
        return keywords.length > 0 ? keywords.join(', ') : 'Nenhuma palavra';
      case 'regex':
        return (value.pattern as string) || 'Sem padrão';
      case 'first_message':
        return 'Qualquer primeira mensagem';
      case 'contact_tag':
        return 'Tags específicas';
      case 'time_range':
        const start = value.start as string;
        const end = value.end as string;
        return start && end ? `${start} - ${end}` : 'Horário não definido';
      case 'webhook':
        return 'Gatilho externo';
      default:
        return '';
    }
  };

  // Descrição da ação
  const getActionDescription = (rule: RoutingRule): string => {
    const value = rule.action_value;
    switch (rule.action_type) {
      case 'assign_department':
        return 'Departamento específico';
      case 'assign_agent':
        return 'Agente específico';
      case 'add_tag':
        return 'Tag específica';
      case 'set_priority':
        return `Prioridade: ${value.priority || 0}`;
      case 'assign_phase':
        return 'Fase do Kanban';
      case 'send_text':
        const msg = (value.message as string) || '';
        return `Texto: "${msg.length > 25 ? msg.substring(0, 25) + '...' : msg}"`;
      case 'send_image':
        return `Enviar imagem${value.caption ? ' (com legenda)' : ''}`;
      case 'send_video':
        return `Enviar vídeo${value.caption ? ' (com legenda)' : ''}`;
      case 'send_audio':
        return 'Enviar áudio';
      case 'send_document':
        return `Documento: ${(value.filename as string) || 'arquivo'}`;
      case 'send_link':
        return `Link: ${((value.url as string) || '').substring(0, 25)}...`;
      default:
        return 'Ação desconhecida';
    }
  };

  // Descrição das inboxes
  const getInboxesDescription = (rule: RoutingRule): React.ReactNode => {
    if (!rule.inbox_ids || rule.inbox_ids.length === 0) {
      return (
        <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
          <Globe className="h-3 w-3 mr-1" />
          Todas
        </Badge>
      );
    }

    const inboxNames = rule.inbox_ids
      .map(id => inboxesMap?.[id] || id.substring(0, 8))
      .slice(0, 2);
    
    const remaining = rule.inbox_ids.length - 2;

    return (
      <div className="flex flex-wrap gap-1">
        {inboxNames.map((name, idx) => (
          <Badge key={idx} variant="outline" className="text-xs">
            <Inbox className="h-3 w-3 mr-1" />
            {name}
          </Badge>
        ))}
        {remaining > 0 && (
          <Badge variant="outline" className="text-xs text-muted-foreground">
            +{remaining}
          </Badge>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-8 w-32" />
        </div>
        <Skeleton className="h-10 w-full rounded-lg" />
        <div className="rounded-xl border bg-card p-6 space-y-4">
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // v1.1.0: Renderiza a tabela de regras (mesmo layout/colunas/ações da versão flat)
  // - O índice usado para # e para habilitar/desabilitar ↑/↓ vem da lista GLOBAL
  //   `generalRules` para preservar 100% a lógica de prioridade existente.
  const renderRulesTable = (rulesToRender: RoutingRule[]) => (
    <div className="rounded-lg border bg-background/50 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30 hover:bg-muted/30">
            <TableHead className="w-12 font-semibold">#</TableHead>
            <TableHead className="font-semibold">Nome</TableHead>
            <TableHead className="font-semibold">Inboxes</TableHead>
            <TableHead className="font-semibold">Condição</TableHead>
            <TableHead className="font-semibold">Ação</TableHead>
            <TableHead className="w-24 text-center font-semibold">Status</TableHead>
            <TableHead className="w-36 text-right font-semibold">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rulesToRender.map((rule) => {
            // Índice GLOBAL para manter coerência da prioridade entre grupos
            const index = generalRules.findIndex((r) => r.id === rule.id);
            return (
              <TableRow
                key={rule.id}
                className={`transition-colors ${!rule.is_active ? 'opacity-50' : ''}`}
              >
                <TableCell className="font-mono text-sm text-muted-foreground">
                  {index + 1}
                </TableCell>

                <TableCell>
                  <div className="space-y-0.5">
                    <p className="font-medium text-foreground">{rule.name}</p>
                    {rule.description && (
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                        {rule.description}
                      </p>
                    )}
                  </div>
                </TableCell>

                <TableCell>{getInboxesDescription(rule)}</TableCell>

                <TableCell>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-foreground">
                      {conditionTypeLabels[rule.condition_type]}
                    </span>
                    <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                      {getConditionDescription(rule)}
                    </span>
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-foreground">
                      {actionTypeLabels[rule.action_type]}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {getActionDescription(rule)}
                    </span>
                  </div>
                </TableCell>

                <TableCell className="text-center">
                  <Badge
                    variant="outline"
                    className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                  >
                    <StatusDot tone={rule.is_active ? 'emerald' : 'muted'} />
                    {rule.is_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                </TableCell>

                <TableCell>
                  <div className="flex items-center justify-end gap-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => movePriority(rule, 'up')}
                          disabled={index === 0}
                          className="h-8 w-8 transition-colors"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Aumentar prioridade</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => movePriority(rule, 'down')}
                          disabled={index === generalRules.length - 1}
                          className="h-8 w-8 transition-colors"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Diminuir prioridade</TooltipContent>
                    </Tooltip>

                    {(isAdmin || canEdit('routing')) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => toggleRuleActive(rule)}
                          >
                            {rule.is_active ? (
                              <ToggleRight className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {rule.is_active ? 'Desativar' : 'Ativar'}
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {(isAdmin || canEdit('routing')) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingRule(rule)}
                            className="h-8 w-8 transition-colors"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Editar regra</TooltipContent>
                      </Tooltip>
                    )}

                    {(isAdmin || canDelete('routing')) && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setRuleToDelete(rule);
                              setDeleteDialogOpen(true);
                            }}
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Excluir regra</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto - padrão Contatos */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Açoes rápidas/Macros</h1>
          <p className="text-xs text-muted-foreground">
            Configure açoes rápidas e macros para automatizar tarefas em conversas
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(isAdmin || canCreate('routing')) && (
            <Button 
              size="xs"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus className="mr-1.5" />
              Nova Macro
            </Button>
          )}
        </div>
      </div>

      {/* Sistema de Abas */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-xl">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <ListChecks className="h-4 w-4" />
            Geral
            {generalRules.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {generalRules.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="webhook" className="flex items-center gap-2">
            <Webhook className="h-4 w-4" />
            Webhook
            {webhookRules.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs bg-rose-500/10 text-rose-600">
                {webhookRules.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <FolderTree className="h-4 w-4" />
            Categorias
          </TabsTrigger>
        </TabsList>

        {/* Aba Geral */}
        <TabsContent value="general" className="mt-6">
          <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-md">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            
            <CardHeader className="relative pb-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <ListChecks className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Macros</CardTitle>
                    <CardDescription>
                      {generalRules.length === 0
                        ? 'Nenhuma macro configurada ainda'
                        : `${generalRules.length} macro${generalRules.length > 1 ? 's' : ''} configurada${generalRules.length > 1 ? 's' : ''}`
                      }
                    </CardDescription>
                  </div>
                </div>
                {/* v1.1.0: Toggle agrupar por categoria */}
                {generalRules.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    <Label htmlFor="group-by-category" className="text-xs text-muted-foreground cursor-pointer">
                      Agrupar por categoria
                    </Label>
                    <Switch
                      id="group-by-category"
                      checked={groupByCategory}
                      onCheckedChange={setGroupByCategory}
                    />
                  </div>
                )}
              </div>
            </CardHeader>

            
            <CardContent className="relative pt-0">
              {generalRules.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 mb-4">
                    <Route className="h-8 w-8 text-muted-foreground/50" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-1">
                    Nenhuma regra configurada
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Crie uma regra para automatizar a distribuição de conversas entre departamentos e agentes.
                  </p>
                  {(isAdmin || canCreate('routing')) && (
                    <Button 
                      onClick={() => setCreateDialogOpen(true)}
                      variant="outline"
                      size="xs"
                      className="mt-4 transition-all duration-200 hover:shadow-sm"
                    >
                      <Plus className="mr-1.5" />
                      Criar primeira regra
                    </Button>
                  )}
                </div>
              ) : groupByCategory && generalRulesGrouped.length > 1 ? (
                /* v1.1.0: Agrupamento por categoria — cada grupo é um Collapsible
                 * com header (ícone/cor/nome/contagem) e a mesma tabela interna. */
                <div className="space-y-3">
                  {generalRulesGrouped.map((group) => (
                    <Collapsible
                      key={group.key}
                      open={isGroupOpen(group.key)}
                      onOpenChange={() => toggleGroupOpen(group.key)}
                      className="rounded-lg border bg-background/40 overflow-hidden"
                    >
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="flex h-8 w-8 items-center justify-center rounded-md shrink-0"
                              style={{
                                backgroundColor: group.color ? `${group.color}1a` : 'hsl(var(--muted))',
                                color: group.color || 'hsl(var(--muted-foreground))',
                              }}
                            >
                              {group.isUncategorized ? (
                                <FolderTree className="h-4 w-4" />
                              ) : (
                                <RoutingCategoryIcon name={group.icon} size={16} />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-foreground truncate">
                                {group.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {group.rules.length} macro{group.rules.length > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                              {group.rules.length}
                            </Badge>
                            <ChevronDown
                              className={`h-4 w-4 text-muted-foreground transition-transform ${
                                isGroupOpen(group.key) ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-3 pb-3">
                          {renderRulesTable(group.rules)}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              ) : (
                renderRulesTable(generalRules)
              )}

            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Webhook */}
        <TabsContent value="webhook" className="mt-6">
          <WebhookRulesList
            rules={webhookRules}
            inboxesMap={inboxesMap || {}}
            loading={false}
            canCreate={isAdmin || canCreate('routing')}
            canEdit={isAdmin || canEdit('routing')}
            canDelete={isAdmin || canDelete('routing')}
            onCreateRule={() => setCreateDialogOpen(true)}
            onEditRule={(rule) => setWebhookDetailRule(rule)}
            onTestRule={(rule) => setWebhookTestRule(rule)}
            onDeleteRule={(rule) => {
              setRuleToDelete(rule as unknown as RoutingRule);
              setDeleteDialogOpen(true);
            }}
            onToggleActive={(rule) => toggleRuleActive(rule as unknown as RoutingRule)}
          />
        </TabsContent>

        {/* Aba Categorias */}
        <TabsContent value="categories" className="mt-6">
          <RoutingCategoriesTab />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      
      {/* Dialog de criar regra */}
      <CreateRuleDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          setCreateDialogOpen(false);
          fetchRules();
        }}
      />

      {/* Dialog de editar regra geral */}
      {editingRule && (
        <EditRuleDialog
          open={!!editingRule}
          onOpenChange={(open) => !open && setEditingRule(null)}
          rule={editingRule}
          onSuccess={() => {
            setEditingRule(null);
            fetchRules();
          }}
        />
      )}

      {/* Dialog de detalhes do webhook */}
      {webhookDetailRule && (
        <WebhookRuleDetailDialog
          open={!!webhookDetailRule}
          onOpenChange={(open) => !open && setWebhookDetailRule(null)}
          rule={webhookDetailRule}
          canEdit={isAdmin || canEdit('routing')}
          canDelete={isAdmin || canDelete('routing')}
          onSuccess={() => {
            setWebhookDetailRule(null);
            fetchRules();
          }}
          onDelete={async () => {
            try {
              const { error } = await supabase.rpc('delete_routing_rule', {
                p_rule_id: webhookDetailRule.id,
                p_tenant_hint: currentTenant?.id,
              });

              if (error) throw error;

              toast.success('Webhook excluído');
              setWebhookDetailRule(null);
              fetchRules();
            } catch (error) {
              routingLogger.error('Erro ao excluir webhook', error);
              toast.error('Erro ao excluir webhook');
            }
          }}
        />
      )}

      {/* Dialog de teste de webhook */}
      {webhookTestRule && webhookTestRule.webhook_token && (
        <WebhookRuleTestDialog
          open={!!webhookTestRule}
          onOpenChange={(open) => !open && setWebhookTestRule(null)}
          webhookToken={webhookTestRule.webhook_token}
          fieldMapping={webhookTestRule.field_mapping || undefined}
        />
      )}

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir regra de roteamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a regra "{ruleToDelete?.name}"? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RoutingRules;
